// src/components/strumenti/OrdineCaviFornitore.js
// Da elenco cavi di cantiere a ordine fornitore raggruppato — stessa logica di un foglio
// Excel diffuso in azienda (colonne Quadro/Pos./Sigla/Descrizione/Tensione/Interr./Lunghezza/
// Tipo/Colore/Formazione/Origine/Destinaz./Note), qui senza i limiti di righe imposti da un
// foglio di calcolo e con la lista "Sigle Cavi Incluse" per gruppo che nell'originale non si
// poteva costruire con sole formule. Strumento standalone, non legato a una commessa.
import React, { useState, useRef } from "react";
import { Plus, Trash2, Upload, Printer, RefreshCw, AlertTriangle } from "lucide-react";
import * as XLSX from "xlsx";

const COLONNE = [
  { key: "quadro", label: "Quadro", w: 90 },
  { key: "pos", label: "Pos.", w: 60 },
  { key: "sigla", label: "Sigla Cavo", w: 110 },
  { key: "descrizione", label: "Descrizione Collegamento", w: 200 },
  { key: "tensione", label: "Tensione", w: 90 },
  { key: "interr", label: "Interr.", w: 80 },
  { key: "lunghezza", label: "L. (mt.)", w: 90 },
  { key: "tipo", label: "Tipo Cavo", w: 130 },
  { key: "colore", label: "Colore", w: 100 },
  { key: "formazione", label: "Formazione", w: 100 },
  { key: "origine", label: "Origine", w: 110 },
  { key: "destinazione", label: "Destinaz.", w: 110 },
  { key: "note", label: "Note", w: 150 },
];

// Intestazioni attese in un file Excel importato, nello stesso ordine — stessa struttura del
// foglio da cui nasce questo strumento. Confronto senza distinguere maiuscole/minuscole o spazi
// superflui, ma l'ordine e i nomi devono corrispondere: non è un'importazione "intelligente"
// che indovina le colonne, è un formato preciso da rispettare.
const INTESTAZIONI_ATTESE = ["Quadro","Pos.","Sigla Cavo","Descrizione Collegamento","Tensione","Interr.","L. (mt.)","Tipo Cavo","Colore","Formazione","Origine","Destinaz.","Note"];

function rigaVuota() {
  return { quadro:"", pos:"", sigla:"", descrizione:"", tensione:"", interr:"", lunghezza:"", tipo:"", colore:"", formazione:"", origine:"", destinazione:"", note:"" };
}

const inp = { width:"100%", padding:"5px 6px", border:"1px solid #e5e7eb", borderRadius:5, fontSize:11.5, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" };

// Testo scritto liberamente dall'utente (o importato da Excel) interpolato nella stampa —
// mai senza escape, altrimenti del testo con dentro codice finirebbe eseguito nel browser
// di chi apre il PDF.
function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[c]));
}

export default function OrdineCaviFornitore() {
  const [riferimento, setRiferimento] = useState("");
  const [righe, setRighe] = useState([rigaVuota(), rigaVuota(), rigaVuota()]);
  const [erroreImport, setErroreImport] = useState(null);
  const fileInputRef = useRef(null);

  const aggiornaCella = (i, key, val) => {
    setRighe(r => r.map((row, idx) => idx === i ? { ...row, [key]: val } : row));
  };
  const aggiungiRiga = () => setRighe(r => [...r, rigaVuota()]);
  const eliminaRiga = (i) => setRighe(r => r.filter((_, idx) => idx !== i));
  const azzeraTutto = () => { setRighe([rigaVuota(), rigaVuota(), rigaVuota()]); setRiferimento(""); setErroreImport(null); };

  // Importazione — richiede esattamente le stesse 13 colonne, nello stesso ordine, del
  // formato originale: non è un'importazione che indovina le colonne, è un formato preciso
  // da rispettare, come da avviso a schermo.
  const importaExcel = (file) => {
    setErroreImport(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array", cellDates: false });
        const nomeFoglio = wb.SheetNames.includes("Dati Cavi") ? "Dati Cavi" : wb.SheetNames[0];
        const foglio = wb.Sheets[nomeFoglio];
        const aoa = XLSX.utils.sheet_to_json(foglio, { header: 1, defval: "" });
        if (aoa.length < 1) { setErroreImport("Il file sembra vuoto."); return; }
        const intestazioniTrovate = aoa[0].map(h => String(h||"").trim());
        const normalizzaTesto = s => s.toLowerCase().replace(/\s+/g," ").trim();
        const attesoNorm = INTESTAZIONI_ATTESE.map(normalizzaTesto);
        const trovatoNorm = intestazioniTrovate.slice(0, INTESTAZIONI_ATTESE.length).map(normalizzaTesto);
        const corrisponde = attesoNorm.length === trovatoNorm.length && attesoNorm.every((h,i) => h === trovatoNorm[i]);
        if (!corrisponde) {
          setErroreImport(`Le colonne del file non corrispondono al formato atteso. Attese, nell'ordine: ${INTESTAZIONI_ATTESE.join(" · ")}. Trovate: ${intestazioniTrovate.filter(Boolean).join(" · ") || "(nessuna intestazione riconosciuta)"}.`);
          return;
        }
        const nuoveRighe = aoa.slice(1)
          .filter(r => r.some(c => String(c||"").trim() !== ""))
          .map(r => ({
            quadro: String(r[0]??""), pos: String(r[1]??""), sigla: String(r[2]??""), descrizione: String(r[3]??""),
            tensione: String(r[4]??""), interr: String(r[5]??""), lunghezza: String(r[6]??""), tipo: String(r[7]??""),
            colore: String(r[8]??""), formazione: String(r[9]??""), origine: String(r[10]??""), destinazione: String(r[11]??""), note: String(r[12]??""),
          }));
        if (nuoveRighe.length === 0) { setErroreImport("Il file ha le colonne giuste ma nessuna riga di dati sotto l'intestazione."); return; }
        setRighe(nuoveRighe);
      } catch (err) {
        console.error("Errore lettura file cavi:", err);
        setErroreImport("Il file non è leggibile come Excel valido.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Raggruppamento — stessa logica del foglio originale: una riga entra nel conteggio solo
  // se ha Tipo Cavo, Formazione e Lunghezza; il gruppo è la combinazione Tipo|Colore|Formazione
  // (colore compreso: due cavi identici per tipo e formazione ma uno con colore e uno senza
  // finiscono in due gruppi diversi, esattamente come nel foglio Excel — se serve un solo
  // gruppo, il colore va compilato in modo coerente su tutte le righe).
  const gruppi = [];
  const mappaGruppi = {};
  const incomplete = [];
  righe.forEach((r, idx) => {
    const haDati = r.sigla.trim() !== "" || r.descrizione.trim() !== "";
    const lunghezzaNum = parseFloat((r.lunghezza||"").toString().replace(",","."));
    const valida = r.tipo.trim() !== "" && r.formazione.trim() !== "" && !isNaN(lunghezzaNum) && lunghezzaNum > 0;
    if (haDati && !valida) {
      incomplete.push({ ...r, _idx: idx });
      return;
    }
    if (!valida) return;
    const chiave = `${r.tipo.trim().toLowerCase()}|${r.colore.trim().toLowerCase()}|${r.formazione.trim().toLowerCase()}`;
    if (!mappaGruppi[chiave]) {
      mappaGruppi[chiave] = { tipo: r.tipo.trim(), colore: r.colore.trim(), formazione: r.formazione.trim(), collegamenti: 0, lunghezzaTotale: 0, sigle: [] };
      gruppi.push(mappaGruppi[chiave]);
    }
    mappaGruppi[chiave].collegamenti += 1;
    mappaGruppi[chiave].lunghezzaTotale += lunghezzaNum;
    if (r.sigla.trim()) mappaGruppi[chiave].sigle.push(r.sigla.trim());
  });

  const totaleMetri = gruppi.reduce((s,g) => s + g.lunghezzaTotale, 0);

  const stampaPDF = () => {
    const win = window.open("", "_blank");
    const oggi = new Date().toLocaleDateString("it-IT", { day:"numeric", month:"long", year:"numeric" });
    const righeTabella = gruppi.map(g => `<tr>
        <td>${escapeHtml(g.tipo)}</td><td>${escapeHtml(g.colore)||"—"}</td><td>${escapeHtml(g.formazione)}</td>
        <td style="text-align:center;">${g.collegamenti}</td>
        <td style="text-align:right;">${g.lunghezzaTotale.toLocaleString("it-IT",{maximumFractionDigits:1})}</td>
        <td style="font-size:9.5px;color:#6b7280;">${g.sigle.map(escapeHtml).join(", ")}</td>
      </tr>`).join("");
    const righeIncomplete = incomplete.length ? `
      <h2 style="color:#92400e;">⚠ Collegamenti con dati mancanti — da verificare prima dell'ordine</h2>
      <table><tr><th>Quadro</th><th>Sigla Cavo</th><th>Descrizione</th><th>Tipo Cavo</th><th>Formazione</th><th>L. (mt.)</th></tr>
      ${incomplete.map(r => `<tr><td>${escapeHtml(r.quadro)||"—"}</td><td>${escapeHtml(r.sigla)||"—"}</td><td>${escapeHtml(r.descrizione)||"—"}</td><td>${escapeHtml(r.tipo)||"—"}</td><td>${escapeHtml(r.formazione)||"—"}</td><td>${escapeHtml(r.lunghezza)||"—"}</td></tr>`).join("")}
      </table>` : "";
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Ordine Cavi Fornitore — ${escapeHtml(riferimento)}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;color:#111;padding:14mm;font-size:12px;}
      @media screen{body{max-width:900px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4;margin:12mm;}
      h1{font-size:18px;color:#7c3aed;margin-bottom:2px;}
      h2{font-size:13px;color:#92400e;margin:18px 0 8px;}
      .sub{font-size:11px;color:#6b7280;margin-bottom:16px;}
      table{width:100%;border-collapse:collapse;margin-bottom:10px;}
      td,th{border:1px solid #e5e7eb;padding:6px 8px;text-align:left;font-size:11px;}
      th{background:#f9fafb;color:#374151;}
      .totale{background:#faf5ff;border:1px solid #e9d5ff;border-radius:8px;padding:10px 14px;font-size:13px;font-weight:700;color:#6d28d9;margin-top:6px;}
    </style></head><body>
      <h1>🗂️ Elenco Cavi da Ordinare al Fornitore</h1>
      <div class="sub">${riferimento ? `Riferimento: ${escapeHtml(riferimento)} · ` : ""}Generato il ${oggi} con Nexiora — Strumenti Tecnici</div>
      <table>
        <tr><th>Tipo Cavo</th><th>Colore</th><th>Formazione</th><th>N. Collegamenti</th><th>Lunghezza Tot. (m)</th><th>Sigle cavi incluse</th></tr>
        ${righeTabella || '<tr><td colspan="6" style="text-align:center;color:#9ca3af;">Nessun gruppo da ordinare</td></tr>'}
      </table>
      <div class="totale">Totale metratura: ${totaleMetri.toLocaleString("it-IT",{maximumFractionDigits:1})} m</div>
      ${righeIncomplete}
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div>
      <div style={{ background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:8, padding:"8px 12px", marginBottom:14, fontSize:11, color:"#6d28d9" }}>
        💜 Ogni riga è un collegamento cavo. Compilando Tipo Cavo, Formazione e Lunghezza, Nexiora li raggruppa da solo per Tipo/Colore/Formazione — quello che vedi sotto è già l'elenco pronto per il fornitore. Attenzione: un cavo con colore indicato e uno identico senza colore vengono trattati come due articoli diversi.
      </div>

      <div style={{ display:"flex", gap:10, alignItems:"flex-end", marginBottom:14, flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:200 }}>
          <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 }}>Riferimento (commessa, cliente... — facoltativo, usato in stampa)</label>
          <input value={riferimento} onChange={e=>setRiferimento(e.target.value)} style={inp} placeholder="Es. Commessa 55-26"/>
        </div>
        <button onClick={() => fileInputRef.current?.click()}
          style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:7, border:"1px solid #e5e7eb", background:"#fff", color:"#374151", fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
          <Upload size={13}/> Importa da Excel
        </button>
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display:"none" }}
          onChange={e => { if (e.target.files[0]) importaExcel(e.target.files[0]); e.target.value=""; }} />
      </div>

      {erroreImport && (
        <div style={{ display:"flex", gap:8, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 12px", marginBottom:14, fontSize:11.5, color:"#991b1b" }}>
          <AlertTriangle size={15} style={{ flexShrink:0, marginTop:1 }}/>
          <div>
            <strong>Importazione non riuscita — le colonne devono corrispondere esattamente.</strong>
            <div style={{ marginTop:4 }}>{erroreImport}</div>
          </div>
        </div>
      )}

      <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:6 }}>Elenco cavi</div>
      <div style={{ overflowX:"auto", border:"1px solid #e5e7eb", borderRadius:8, marginBottom:8 }}>
        <table style={{ borderCollapse:"collapse", fontSize:11.5, minWidth:1400 }}>
          <thead>
            <tr style={{ background:"#f9fafb" }}>
              {COLONNE.map(c => <th key={c.key} style={{ padding:"6px 6px", textAlign:"left", color:"#6b7280", fontWeight:600, minWidth:c.w, borderBottom:"1px solid #e5e7eb" }}>{c.label}</th>)}
              <th style={{ width:32 }}></th>
            </tr>
          </thead>
          <tbody>
            {righe.map((r, i) => (
              <tr key={i}>
                {COLONNE.map(c => (
                  <td key={c.key} style={{ padding:3 }}>
                    <input value={r[c.key]} onChange={e=>aggiornaCella(i, c.key, e.target.value)} style={inp} />
                  </td>
                ))}
                <td style={{ padding:3, textAlign:"center" }}>
                  <button onClick={()=>eliminaRiga(i)} style={{ background:"none", border:"none", cursor:"pointer", color:"#dc2626", padding:2 }}><Trash2 size={13}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={aggiungiRiga} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11.5, fontWeight:700, color:"#7c3aed", background:"none", border:"none", cursor:"pointer", marginBottom:20 }}>
        <Plus size={13}/> Aggiungi riga
      </button>

      <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:8 }}>Ordine per il fornitore — raggruppato</div>
      {gruppi.length === 0 ? (
        <div style={{ textAlign:"center", padding:24, color:"#9ca3af", fontSize:12 }}>Compila Tipo Cavo, Formazione e Lunghezza su almeno una riga per vedere l'ordine raggruppato.</div>
      ) : (
        <div style={{ overflowX:"auto", border:"1px solid #e5e7eb", borderRadius:8, marginBottom:10 }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#faf5ff" }}>
                <th style={{ padding:"7px 10px", textAlign:"left", color:"#6d28d9" }}>Tipo Cavo</th>
                <th style={{ padding:"7px 10px", textAlign:"left", color:"#6d28d9" }}>Colore</th>
                <th style={{ padding:"7px 10px", textAlign:"left", color:"#6d28d9" }}>Formazione</th>
                <th style={{ padding:"7px 10px", textAlign:"right", color:"#6d28d9" }}>N. Collegamenti</th>
                <th style={{ padding:"7px 10px", textAlign:"right", color:"#6d28d9" }}>Lunghezza Tot. (m)</th>
                <th style={{ padding:"7px 10px", textAlign:"left", color:"#6d28d9" }}>Sigle cavi incluse</th>
              </tr>
            </thead>
            <tbody>
              {gruppi.map((g,i) => (
                <tr key={i} style={{ borderTop:"1px solid #f3f4f6" }}>
                  <td style={{ padding:"7px 10px", fontWeight:600 }}>{g.tipo}</td>
                  <td style={{ padding:"7px 10px", color:"#6b7280" }}>{g.colore||"—"}</td>
                  <td style={{ padding:"7px 10px" }}>{g.formazione}</td>
                  <td style={{ padding:"7px 10px", textAlign:"right" }}>{g.collegamenti}</td>
                  <td style={{ padding:"7px 10px", textAlign:"right", fontWeight:700 }}>{g.lunghezzaTotale.toLocaleString("it-IT",{maximumFractionDigits:1})}</td>
                  <td style={{ padding:"7px 10px", color:"#9ca3af", fontSize:11 }}>{g.sigle.length ? g.sigle.join(", ") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {incomplete.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12.5, fontWeight:700, color:"#92400e", marginBottom:6 }}>
            <AlertTriangle size={14}/> {incomplete.length} collegament{incomplete.length>1?"i":"o"} con dati mancanti — da verificare prima dell'ordine
          </div>
          <div style={{ overflowX:"auto", border:"1px solid #fde68a", borderRadius:8 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11.5 }}>
              <thead>
                <tr style={{ background:"#fffbeb" }}>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>Quadro</th>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>Sigla Cavo</th>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>Descrizione</th>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>Tipo Cavo</th>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>Formazione</th>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>L. (mt.)</th>
                </tr>
              </thead>
              <tbody>
                {incomplete.map((r,i) => (
                  <tr key={i} style={{ borderTop:"1px solid #fef3c7" }}>
                    <td style={{ padding:"6px 10px" }}>{r.quadro||"—"}</td>
                    <td style={{ padding:"6px 10px" }}>{r.sigla||"—"}</td>
                    <td style={{ padding:"6px 10px" }}>{r.descrizione||"—"}</td>
                    <td style={{ padding:"6px 10px" }}>{r.tipo||"—"}</td>
                    <td style={{ padding:"6px 10px" }}>{r.formazione||"—"}</td>
                    <td style={{ padding:"6px 10px" }}>{r.lunghezza||"—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", borderTop:"1px solid #f3f4f6", paddingTop:14 }}>
        <button onClick={azzeraTutto} style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
          <RefreshCw size={12}/> Azzera tutto
        </button>
        <button onClick={stampaPDF} disabled={gruppi.length===0}
          title={gruppi.length===0 ? "Compila almeno un gruppo per stampare" : "Stampa PDF"}
          style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700,
            color: gruppi.length>0 ? "#7c3aed" : "#d1d5db",
            background: gruppi.length>0 ? "#faf5ff" : "#f9fafb",
            border:`1px solid ${gruppi.length>0?"#e9d5ff":"#e5e7eb"}`, borderRadius:8, padding:"9px 18px",
            cursor: gruppi.length>0 ? "pointer" : "not-allowed" }}>
          <Printer size={13}/> Stampa PDF
        </button>
      </div>
    </div>
  );
}
